import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useSession } from '@/features/auth/hooks/useSession';
import { api } from '@/lib/api';
import { useHideDemo } from './demoVisibility';

type DemoStatus =
  | { available: false; loaded: false; counts: null }
  | { available: true; loaded: boolean; counts: Record<string, number> };

const DEMO_SOURCE_PREFIX = 'demo-';

function isDemoSourced(obj: unknown): boolean {
  if (!obj || typeof obj !== 'object') return false;
  const attrs = (obj as { attributes?: { source?: unknown } }).attributes;
  const src = attrs?.source ?? (obj as { source?: unknown }).source;
  return typeof src === 'string' && src.startsWith(DEMO_SOURCE_PREFIX);
}

/**
 * Reads the workspace's demo-seed status (shared cache key with SidebarRail
 * and RemoveDemoDataButton — no extra fetch).
 *
 * Returns a filter function the caller can apply to any list of objects:
 *   - When demo is LOADED: show everything (demo + real user/agent items
 *     coexist until the user clicks "Remove demo data"). See #2459.
 *   - When demo is NOT loaded: drop any leftover demo-sourced items (cleanup
 *     path for stale demo rows still in caches).
 *   - When demo is UNAVAILABLE (prod / 400 / 401 / 403 / 404): no filtering.
 */
export function useDemoMode() {
  const { token } = useSession();

  const { data: demoStatus, refetch } = useQuery<DemoStatus>({
    queryKey: ['demo-seed', 'status'],
    queryFn: async () => {
      try {
        const { data } = await api.http.get<{ loaded: boolean; counts: Record<string, number> }>('/demo-seed/status');
        return { available: true, loaded: data.loaded, counts: data.counts };
      } catch (err: any) {
        if (err?.status === 400 || err?.status === 401 || err?.status === 403 || err?.status === 404) {
          return { available: false, loaded: false, counts: null };
        }
        throw err;
      }
    },
    enabled: Boolean(token),
    staleTime: 10_000,
  });

  const loaded = Boolean(demoStatus?.available && demoStatus.loaded);
  const available = Boolean(demoStatus?.available);
  const [hidden, setHidden] = useHideDemo();

  const filterItems = useCallback(
    <T,>(items: T[] | undefined): T[] => {
      if (!items || !available) return items ?? [];
      // User toggled "hide demo data" → drop demo-sourced rows even while loaded.
      // Mutually-exclusive views — demo and the user's real data are never
      // shown together. Switching only filters; nothing is deleted.
      //   user view  (hidden)        → only real data (drop demo-sourced)
      //   demo view  (loaded+shown)  → only demo data (drop real)
      //   not seeded                 → only real data (cleanup stale demo rows)
      if (hidden) return items.filter((i) => !isDemoSourced(i));
      if (loaded) return items.filter((i) => isDemoSourced(i));
      return items.filter((i) => !isDemoSourced(i));
    },
    [available, loaded, hidden],
  );

  // Demo is actively on screen (seeded + not hidden). When true the header
  // offers "Hide demo"; otherwise it offers to show/load it.
  const showingDemo = available && loaded && !hidden;

  return { loaded, available, hidden, setHidden, showingDemo, filterItems, refetch };
}

/**
 * Add / remove demo data: purge wipes ALL demo data (emails + objects); load
 * re-seeds it. Both invalidate the relevant caches so every module refreshes.
 * Used by the header toolbar button and the onboarding "In Conclusion" link.
 */
export function useDemoData() {
  const { loaded, available, hidden, setHidden, showingDemo, refetch } = useDemoMode();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  // Synchronous in-flight guard — a ref never goes stale the way a `busy` value
  // captured in the callback's deps would, so a fast double-click can't double-fire.
  const inFlightRef = useRef(false);

  const run = useCallback(
    async (path: '/demo-seed/purge' | '/demo-seed/load') => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setBusy(true);
      try {
        await api.http.post(path, {});
        // Purge/load rewrites the whole dataset (objects, emails, integrations),
        // so refetch everything rather than guess which keys changed.
        await queryClient.invalidateQueries();
        window.dispatchEvent(new CustomEvent('xavior:demo-data-loaded'));
        await refetch();
      } catch (err) {
        console.error('[useDemoData]', path, err);
      } finally {
        inFlightRef.current = false;
        setBusy(false);
      }
    },
    [queryClient, refetch],
  );

  const remove = useCallback(() => run('/demo-seed/purge'), [run]);
  const add = useCallback(() => run('/demo-seed/load'), [run]);

  // Reversible view switches — filter only, never delete. Demo and real data
  // are mutually exclusive on screen (see useDemoMode.filterItems).
  const hideDemo = useCallback(() => setHidden(true), [setHidden]);
  const showDemo = useCallback(async () => {
    if (!loaded) await run('/demo-seed/load'); // seed on first show
    setHidden(false);
  }, [loaded, run, setHidden]);

  return { loaded, available, hidden, showingDemo, busy, remove, add, hideDemo, showDemo };
}

/**
 * Wraps a react-query result with demo-mode filtering, delegating to the
 * `filterItems` function returned by `useDemoMode`. Per #2459:
 *   - When demo is LOADED: real user/agent items and demo items coexist
 *     (pass-through; nothing is hidden).
 *   - When demo is NOT loaded: residual `demo-*` sourced rows still in
 *     caches are filtered out (cleanup path).
 *   - When demo-seed is unavailable (prod / 400 / 401 / 403 / 404): returns the
 *     original result unchanged.
 *
 * Centralises the memoised filter-and-wrap pattern that was duplicated in
 * both useObjectsByType implementations (hooks.ts and hooks/queries.ts).
 */
export function useDemoFilteredResult<T>(
  result: UseQueryResult<{ items?: T[] }, unknown>,
  filterItems: (items: T[]) => T[],
): UseQueryResult<{ items?: T[] }, unknown> {
  const items = result.data?.items;
  const filteredItems = useMemo(
    () => (items ? filterItems(items) : items),
    [items, filterItems],
  );
  const wrappedData = useMemo(() => {
    if (!result.data || filteredItems === items) return result.data;
    return { ...result.data, items: filteredItems };
  }, [result.data, filteredItems, items]);
  if (wrappedData === result.data) return result;
  return { ...result, data: wrappedData } as UseQueryResult<{ items?: T[] }, unknown>;
}
